import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronLeft, ChevronRight, History } from "lucide-react";
import { makeReminder, useToggleReminder } from "@/components/Reminders";
import { archived } from "@/components/LiveInfo";
import { useStore } from "@/lib/store";
import { useXC, useNow } from "@/lib/hooks";
import { asList, b64, fmtTime } from "@/lib/xc";

const PX_PER_MIN = 5;
const SPAN_MIN = 240;
const PAGE = 25;

function EpgRow({ ch, start, end, onWatch }) {
  const { active, hasReminder } = useStore();
  const toggleReminder = useToggleReminder();
  const chItem = { type: "live", id: ch.stream_id, name: ch.name, icon: ch.stream_icon, category_id: ch.category_id, archive: Number(ch.tv_archive) === 1, archiveDays: Number(ch.tv_archive_duration) || 0 };
  const isArchive = Number(ch.tv_archive) === 1;
  const arch = { archiveDays: Number(ch.tv_archive_duration) || 1 };
  const { data } = useXC(active, isArchive ? "get_simple_data_table" : "get_short_epg", isArchive ? { stream_id: ch.stream_id } : { stream_id: ch.stream_id, limit: 8 });
  const progs = asList(data?.epg_listings)
    .map((p) => ({ ...p, s: Number(p.start_timestamp) * 1000, e: Number(p.stop_timestamp) * 1000 }))
    .filter((p) => p.e > start && p.s < end);
  const now = Date.now();
  return (
    <div className="flex border-b border-[#212738] h-16" data-testid={`guide-row-${ch.stream_id}`}>
      <button
        onClick={() => onWatch()}
        className="sticky left-0 z-10 w-[220px] shrink-0 flex items-center gap-3 px-4 bg-[#0B0D14] border-r border-[#212738] hover:bg-[#11141F] text-left"
        data-testid={`guide-channel-${ch.stream_id}`}
      >
        <div className="w-9 h-9 rounded-md bg-[#1A1E2E] overflow-hidden shrink-0">
          {ch.stream_icon && <img src={ch.stream_icon} alt="" loading="lazy" className="w-full h-full object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />}
        </div>
        <span className="text-sm truncate flex-1">{ch.name}</span>
        {isArchive && <History className="w-3.5 h-3.5 text-[#00E5FF] shrink-0" />}
      </button>
      <div className="relative" style={{ width: SPAN_MIN * PX_PER_MIN }}>
        {!progs.length && <div className="absolute inset-y-2 left-2 right-2 rounded-lg border border-dashed border-[#212738] grid place-items-center text-xs text-slate-600">No guide data</div>}
        {progs.map((p, i) => {
          const left = (Math.max(p.s, start) - start) / 60000 * PX_PER_MIN;
          const width = (Math.min(p.e, end) - Math.max(p.s, start)) / 60000 * PX_PER_MIN;
          const live = p.s <= now && p.e > now;
          const past = isArchive && archived(p, arch, now);
          const future = p.s > now;
          const rem = future && hasReminder(`${ch.stream_id}:${p.start_timestamp}`);
          return (
            <button
              key={i}
              onClick={() => (future ? toggleReminder(makeReminder(chItem, p)) : past ? onWatch({ ...p, titleText: b64(p.title) }) : onWatch())}
              title={future ? (rem ? "Click to remove reminder" : "Click to get a reminder when this starts") : b64(p.description)}
              data-testid={past ? `guide-catchup-${ch.stream_id}-${p.start_timestamp}` : future ? `guide-prog-${ch.stream_id}-${p.start_timestamp}` : `guide-live-${ch.stream_id}`}
              className={`absolute top-1.5 bottom-1.5 rounded-lg px-3 text-left overflow-hidden border transition-colors ${rem ? "bg-[#FFB800]/10 border-[#FFB800]/50" : live ? "bg-[#E50914]/15 border-[#E50914]/50 hover:bg-[#E50914]/25" : past ? "bg-[#00E5FF]/5 border-[#00E5FF]/30 hover:bg-[#00E5FF]/15" : p.e < now ? "bg-[#0B0D14] border-[#1A1E2E] opacity-60" : "bg-[#11141F] border-[#212738] hover:border-[#38425C]"}`}
              style={{ left: left + 2, width: Math.max(width - 4, 8) }}
            >
              <div className="text-xs font-medium truncate flex items-center gap-1">{past && <History className="w-3 h-3 text-[#00E5FF] shrink-0" />}{rem && <Bell className="w-3 h-3 text-[#FFB800] fill-[#FFB800] shrink-0" data-testid={`guide-reminder-icon-${ch.stream_id}-${p.start_timestamp}`} />}{b64(p.title)}</div>
              <div className="text-[10px] font-mono text-slate-500 truncate">{fmtTime(p.start_timestamp)} – {fmtTime(p.stop_timestamp)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Guide() {
  const { active } = useStore();
  const nav = useNavigate();
  const now = useNow(60000);
  const cats = asList(useXC(active, "get_live_categories").data);
  const [cat, setCat] = useState(null);
  const curCat = cat ?? cats[0]?.category_id;
  const streams = useXC(active, "get_live_streams", { category_id: curCat }, !!curCat);
  const [count, setCount] = useState(PAGE);
  const [offset, setOffset] = useState(0);
  const list = asList(streams.data);
  const start = useMemo(() => Math.floor((now - 30 * 60000) / 1800000) * 1800000 + offset * 3 * 3600000, [now, offset]);
  const end = start + SPAN_MIN * 60000;
  const slots = Array.from({ length: SPAN_MIN / 30 }, (_, i) => start + i * 1800000);
  const nowLeft = (now - start) / 60000 * PX_PER_MIN;

  return (
    <div className="h-full flex flex-col" data-testid="guide-page">
      <div className="px-8 py-5 border-b border-[#212738] flex items-end gap-6">
        <div>
          <div className="xc-label">TV Guide</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">What's on</h1>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setOffset((o) => Math.max(o - 1, -24))} className="xc-btn-ghost !px-2.5 !py-1.5" data-testid="guide-earlier-button" title="Earlier">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setOffset(0)} className={`xc-btn-ghost !py-1.5 text-sm ${offset === 0 ? "!border-[#E50914]/60" : ""}`} data-testid="guide-now-button">
            {offset === 0 ? "Now" : new Date(start).toLocaleDateString([], { weekday: "short", day: "numeric" })}
          </button>
          <button onClick={() => setOffset((o) => Math.min(o + 1, 8))} className="xc-btn-ghost !px-2.5 !py-1.5" data-testid="guide-later-button" title="Later">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 flex gap-2 overflow-x-auto xc-scroll pb-1">
          {cats.map((c) => (
            <button
              key={c.category_id}
              onClick={() => {
                setCat(c.category_id);
                setCount(PAGE);
              }}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm border transition-colors ${c.category_id === curCat ? "bg-[#E50914] border-[#E50914] text-white" : "border-[#212738] text-slate-400 hover:text-white"}`}
              data-testid={`guide-category-${c.category_id}`}
            >
              {c.category_name}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto xc-scroll" data-testid="guide-grid">
        <div className="min-w-max relative">
          <div className="flex sticky top-0 z-20 bg-[#08090E] border-b border-[#212738] h-10">
            <div className="sticky left-0 z-10 w-[220px] shrink-0 bg-[#08090E] border-r border-[#212738]" />
            {slots.map((t) => (
              <div key={t} className="text-xs font-mono text-slate-500 px-2 flex items-center border-l border-[#212738]" style={{ width: 30 * PX_PER_MIN }}>
                {new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            ))}
          </div>
          {nowLeft >= 0 && nowLeft <= SPAN_MIN * PX_PER_MIN && <div className="absolute top-0 bottom-0 w-px bg-[#E50914] z-10 pointer-events-none" style={{ left: 220 + nowLeft }} />}
          {streams.loading && <div className="p-6 text-sm text-slate-500">Loading channels…</div>}
          {list.slice(0, count).map((ch) => (
            <EpgRow
              key={ch.stream_id}
              ch={ch}
              start={start}
              end={end}
              onWatch={(catchup) =>
                nav("/live", {
                  state: {
                    catchup: catchup || null,
                    channel: { type: "live", id: ch.stream_id, name: ch.name, icon: ch.stream_icon, category_id: ch.category_id, archive: Number(ch.tv_archive) === 1, archiveDays: Number(ch.tv_archive_duration) || 0 },
                  },
                })
              }
            />
          ))}
          {count < list.length && (
            <div className="p-6 sticky left-0">
              <button onClick={() => setCount((c) => c + PAGE)} className="xc-btn-ghost" data-testid="guide-load-more">
                Show more channels ({list.length - count} left)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
